package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpUnitRule
import br.com.vvrefeicoes.portal.domain.repository.RmpRequestRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_unit_rules_hint
import portalvv.shared.generated.resources.rmp_unit_rules_only_marked
import portalvv.shared.generated.resources.rmp_unit_rules_search
import portalvv.shared.generated.resources.rmp_unit_rules_title
import portalvv.shared.generated.resources.rmp_unit_rules_waives_ceo

/** Regras do RMP por unidade: marca em quais unidades a admissão dispensa o CEO VV depois da diretoria. */
class RmpUnitRulesController(
    private val repository: RmpRequestRepository,
    private val scope: CoroutineScope,
) {
    var rules by mutableStateOf<List<RmpUnitRule>>(emptyList())
        private set

    var loading by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var query by mutableStateOf("")
        private set

    var onlyMarked by mutableStateOf(false)
        private set

    /** Unidade sendo gravada (a caixa fica desligada até a API responder). */
    var saving by mutableStateOf<String?>(null)
        private set

    val visibleRules: List<RmpUnitRule>
        get() =
            rules.filter { rule ->
                (!onlyMarked || rule.admissionWaivesCeo) &&
                    (query.isBlank() || rule.companyName.contains(query.trim(), ignoreCase = true))
            }

    fun load() {
        loading = true
        errorMessage = null
        scope.launch {
            try {
                rules = repository.loadUnitRules()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar as regras por unidade."
            } finally {
                loading = false
            }
        }
    }

    fun updateQuery(value: String) {
        query = value
    }

    fun toggleOnlyMarked() {
        onlyMarked = !onlyMarked
    }

    fun toggle(rule: RmpUnitRule) {
        if (saving != null) return
        saving = rule.companyId
        errorMessage = null
        scope.launch {
            try {
                val saved = repository.updateUnitRule(rule.companyId, !rule.admissionWaivesCeo)
                rules = rules.map { if (it.companyId == saved.companyId) saved else it }
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível salvar a regra."
            } finally {
                saving = null
            }
        }
    }

    fun dismissError() {
        errorMessage = null
    }
}

@Composable
fun rememberRmpUnitRulesController(): RmpUnitRulesController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RmpUnitRulesController(appGraph.rmpRequestRepository, scope) }
}

@Composable
internal fun RmpUnitRulesDialog(onDismiss: () -> Unit) {
    val controller = rememberRmpUnitRulesController()
    LaunchedEffect(controller) {
        controller.load()
    }
    AppModalCard(title = stringResource(Res.string.rmp_unit_rules_title), onDismiss = onDismiss) {
        Text(
            text = stringResource(Res.string.rmp_unit_rules_hint),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        AppTextField(
            value = controller.query,
            onValueChange = controller::updateQuery,
            placeholder = stringResource(Res.string.rmp_unit_rules_search),
            modifier = Modifier.fillMaxWidth(),
        )
        Row(
            modifier = Modifier.clickable { controller.toggleOnlyMarked() },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Checkbox(checked = controller.onlyMarked, onCheckedChange = { controller.toggleOnlyMarked() })
            Text(stringResource(Res.string.rmp_unit_rules_only_marked), style = MaterialTheme.typography.bodyMedium)
        }
        if (controller.loading) {
            Box(modifier = Modifier.fillMaxWidth().padding(AppSpacing.space24), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
            return@AppModalCard
        }
        val waivesCeo = stringResource(Res.string.rmp_unit_rules_waives_ceo)
        controller.visibleRules.forEach { rule ->
            val enabled = controller.saving == null
            Row(
                modifier = Modifier.fillMaxWidth().clickable(enabled = enabled) { controller.toggle(rule) },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Checkbox(
                    checked = rule.admissionWaivesCeo,
                    onCheckedChange = { controller.toggle(rule) },
                    enabled = enabled,
                )
                Text(text = rule.companyName, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                if (rule.admissionWaivesCeo) {
                    Text(text = waivesCeo, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                }
                if (controller.saving == rule.companyId) {
                    CircularProgressIndicator(modifier = Modifier.padding(AppSpacing.space4))
                }
            }
        }
    }
}
